// Quick Action Panel Configuration
// Maximum buttons allowed in the Quick Action Panel
export const MAX_QUICK_ACTIONS = 12;

// Storage key for localStorage
export const QUICK_ACTIONS_STORAGE_KEY = "bedrock_client_quick_actions";

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  to: string;
  category: string;
  permanent?: boolean; // If true, this action cannot be removed and is always shown first
  isSeparator?: boolean; // If true, this is a visual separator
  createEntity?: string; // If set, opens a create dialog instead of navigating (e.g., "vehicle")
}

// Available actions for the Quick Action Panel
export const AVAILABLE_QUICK_ACTIONS: QuickAction[] = [
  // Dashboards
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Main dashboard with analytics and metrics",
    icon: "element-11",
    to: "/dashboard",
    category: "Dashboards",
    permanent: true, // Always shown first, cannot be removed
  },

  // Create Actions
  {
    id: "create-sale",
    title: "Create Sale",
    description: "Create a new sale entry",
    icon: "basket",
    to: "/sale-new/new",
    category: "Create",
  },
  {
    id: "create-sale-step1",
    title: "Quick Sale Entry",
    description: "Start a 2-step sale entry",
    icon: "rocket",
    to: "/sale-step1",
    category: "Create",
  },
  {
    id: "create-vehicle",
    title: "Create Vehicle",
    description: "Add a new vehicle",
    icon: "car",
    to: "/vehicles",
    category: "Create",
    createEntity: "vehicle", // Opens create dialog without navigating away
  },
  {
    id: "create-customer",
    title: "Create Customer",
    description: "Add a new sales customer",
    icon: "profile-user",
    to: "/sales-customers",
    category: "Create",
    createEntity: "sales-customer", // Opens create dialog without navigating away
  },
  {
    id: "create-supplier",
    title: "Create Supplier",
    description: "Add a new supplier",
    icon: "handcart",
    to: "/suppliers",
    category: "Create",
    createEntity: "supplier", // Opens create dialog without navigating away
  },
  {
    id: "create-purchase",
    title: "Create Purchase",
    description: "Create a new purchase entry",
    icon: "shop",
    to: "/purchase-new/new",
    category: "Create",
  },

  // Sales
  {
    id: "pending-sales",
    title: "Pending Sales",
    description: "View and manage pending sales",
    icon: "time",
    to: "/pending-sales",
    category: "Sales",
  },
  {
    id: "sales-list",
    title: "All Sales",
    description: "View all completed sales",
    icon: "document",
    to: "/sales-list",
    category: "Sales",
  },

  // Purchases
  {
    id: "purchases-list",
    title: "All Purchases",
    description: "View all purchases",
    icon: "shop",
    to: "/purchases-list",
    category: "Purchases",
  },

  // Master Data
  {
    id: "vehicles",
    title: "Vehicles",
    description: "View and manage vehicles",
    icon: "car",
    to: "/vehicles",
    category: "Master Data",
  },
  {
    id: "customers",
    title: "Customers",
    description: "View and manage customers",
    icon: "profile-user",
    to: "/sales-customers",
    category: "Master Data",
  },
  {
    id: "products",
    title: "Products",
    description: "View and manage products",
    icon: "shop",
    to: "/inventory-items",
    category: "Master Data",
  },
  {
    id: "suppliers",
    title: "Suppliers",
    description: "View and manage suppliers",
    icon: "handcart",
    to: "/suppliers",
    category: "Master Data",
  },
  {
    id: "services",
    title: "Services",
    description: "View and manage services",
    icon: "abstract-14",
    to: "/service-items",
    category: "Master Data",
  },

  // System
  {
    id: "sync-status",
    title: "Sync Status",
    description: "View data synchronization status",
    icon: "cloud",
    to: "/sync",
    category: "System",
  },
  {
    id: "users",
    title: "Users",
    description: "View and manage users",
    icon: "user",
    to: "/users",
    category: "System",
  },
  {
    id: "org-settings",
    title: "Settings",
    description: "Organization settings",
    icon: "setting-2",
    to: "/org-settings",
    category: "System",
  },
];

// Default quick actions (shown when no user preference is set)
export const DEFAULT_QUICK_ACTION_IDS: string[] = ["dashboard"];

// Get quick actions grouped by category
export const getQuickActionsByCategory = (): Record<string, QuickAction[]> => {
  return AVAILABLE_QUICK_ACTIONS.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);
};

// Get a quick action by ID
export const getQuickActionById = (id: string): QuickAction | undefined => {
  return AVAILABLE_QUICK_ACTIONS.find((action) => action.id === id);
};

// Get all permanent actions (cannot be removed)
export const getPermanentActions = (): QuickAction[] => {
  return AVAILABLE_QUICK_ACTIONS.filter((action) => action.permanent === true);
};

// Get permanent action IDs
export const getPermanentActionIds = (): string[] => {
  return getPermanentActions().map((action) => action.id);
};

// Separator prefix for identification
export const SEPARATOR_PREFIX = "separator-";

// Create a separator action with unique ID
export const createSeparator = (): QuickAction => {
  return {
    id: `${SEPARATOR_PREFIX}${Date.now()}`,
    title: "Separator",
    description: "Visual separator between buttons",
    icon: "minus",
    to: "",
    category: "Utility",
    isSeparator: true,
  };
};

// Check if an action ID is a separator
export const isSeparatorId = (id: string): boolean => {
  return id.startsWith(SEPARATOR_PREFIX);
};
